import { format, subDays } from "date-fns";
import { RefreshCw } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import { useTriggerResync } from "../api/useTriggerResync";

import { Button } from "@/components/ui/v2/button";
import { DateRangePicker } from "@/components/ui/v2/date-range-picker";
import type { BaseComponentProps } from "@/features/integrations/shared/renderer/types";
import { decodeSlug } from "@/features/integrations/utils/slug";
import useToast from "@/lib/hooks/useToast";

type ResyncButtonProps = {
  missingCount?: number;
};

export function ResyncButton({ missingCount }: ResyncButtonProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const { clientId, integrationName: rawIntegrationName } = useParams<{
    clientId?: string;
    integrationName?: string;
  }>();

  const integrationName = rawIntegrationName
    ? decodeSlug(rawIntegrationName)
    : undefined;

  const [searchParams, setSearchParams] = useSearchParams();

  const defaultFrom = format(subDays(new Date(), 7), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");
  const from = searchParams.get("from") || defaultFrom;
  const to = searchParams.get("to") || defaultTo;

  const resyncMutation = useTriggerResync(integrationName, clientId);

  const handleDateRangeChange = useCallback(
    ({ range }: { range: { from?: Date; to?: Date } }) => {
      const newFrom = range.from ? format(range.from, "yyyy-MM-dd") : from;
      const newTo = range.to ? format(range.to, "yyyy-MM-dd") : to;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("from", newFrom);
          next.set("to", newTo);
          next.set("page", "1");
          return next;
        },
        { replace: true },
      );
    },
    [from, to, setSearchParams],
  );

  const handleResync = useCallback(() => {
    resyncMutation.mutate(
      { from, to },
      {
        onSuccess: () =>
          showToast(
            t("integrations.modules.directApply.sync.resyncQueued", {
              defaultValue:
                "Sync queued — applications will appear here once processed.",
            }),
            "success",
          ),
        onError: (err) =>
          showToast(
            err instanceof Error ? err.message : String(err),
            "error",
          ),
      },
    );
  }, [from, to, resyncMutation, t, showToast]);

  return (
    <>
      <DateRangePicker
        initialDateFrom={from}
        initialDateTo={to}
        maxDate={new Date()}
        onUpdate={handleDateRangeChange}
      />
      <Button
        variant="outline"
        onClick={handleResync}
        // missingCount is undefined when the comparison query failed (partner unavailable). In that
        // exact case resync is the user's recovery action, so don't disable — only disable when we
        // know there is nothing to sync (comparison loaded, zero missing rows).
        disabled={resyncMutation.isPending || missingCount === 0}
      >
        <RefreshCw
          className={`mr-2 h-4 w-4 ${resyncMutation.isPending ? "animate-spin" : ""}`}
        />
        {t("integrations.modules.directApply.sync.resync", {
          defaultValue: "Resync",
        })}
      </Button>
    </>
  );
}

export function ResyncButtonWrapper({ data }: BaseComponentProps) {
  const missingCount = data?.missingCount as number | undefined;
  return <ResyncButton missingCount={missingCount} />;
}
